import { useId, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { apiPost } from '../../api/client.js';
import { useToast } from '../../hooks/useToast.js';

export interface ResetPasswordDialogProps {
  open: boolean;
  userId: string;
  username: string;
  onClose: () => void;
}

export function ResetPasswordDialog({
  open,
  userId,
  username,
  onClose,
}: ResetPasswordDialogProps): ReactElement {
  const { push } = useToast();
  const tempId = useId();
  const [tempPassword, setTempPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function close(): void {
    setTempPassword('');
    setError(null);
    setSubmitting(false);
    onClose();
  }

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (tempPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiPost(`/api/users/${encodeURIComponent(userId)}/reset-pwd`, {
        tempPassword,
      });
      push('success', `Temporary password set for "${username}"`);
      close();
    } catch (err) {
      const apiErr = err as { message?: string };
      push('error', apiErr?.message ?? 'Failed to reset password');
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} title={`Reset password for ${username}`}>
      <form onSubmit={onSubmit} className="flex flex-col gap-3" data-testid="reset-pwd-form">
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={tempId} style={{ color: 'var(--text-muted)' }}>Temporary password (≥ 8 characters)</label>
          <input
            id={tempId}
            type="text"
            value={tempPassword}
            onChange={(e) => setTempPassword(e.target.value)}
            autoComplete="off"
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={{
              background: 'var(--bg-primary)',
              borderColor: error ? '#dc2626' : 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {error ? (
            <span className="text-xs" style={{ color: '#dc2626' }} data-testid="error-temp">
              {error}
            </span>
          ) : null}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="rounded-md border px-4 py-2 text-sm"
            style={{
              background: 'transparent',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
              minHeight: 48,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md px-4 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              color: 'var(--accent-contrast)',
              minHeight: 48,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Saving…' : 'Set password'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}